import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useIsomorphicLayoutEffect } from './compat';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

export interface MenuItemProps {
  label: string;
  icon?: ReactNode;
  shortcut?: string;
  disabled?: boolean;
  destructive?: boolean;
  onClick?: () => void;
}

export interface MenuSeparator {
  type: 'separator';
}

export type MenuEntry = MenuItemProps | MenuSeparator;
type MenuSide = 'top' | 'bottom' | 'left' | 'right';
type MenuAlign = 'start' | 'end';

export interface MenuProps {
  trigger: ReactNode;
  items?: MenuEntry[];
  content?: ReactNode;
  align?: MenuAlign;
  side?: MenuSide;
  className?: string;
  menuClassName?: string;
  itemClassName?: string;
  closeOnSelect?: boolean;
  onOpenChange?: (open: boolean) => void;
  role?: 'menu' | 'dialog';
  ariaLabel?: string;
}

type Position = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  maxWidth: number;
  minWidth: number;
  side: MenuSide;
  ready: boolean;
};

const PAD = 8;
const GAP = 8;
const MIN_WIDTH = 180;
const MIN_VIEWPORT_SIZE = 120;
const BREAKPOINTS: Record<string, number> = { sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 };
const cn = (...values: Array<string | false | null | undefined>) => twMerge(clsx(values));
const isSeparator = (item: MenuEntry): item is MenuSeparator => 'type' in item;
const clamp = (value: number, min: number, max: number) => max < min ? min : Math.min(Math.max(value, min), max);
const utility = (token: string) => token.split(':').pop()?.replace(/^!/, '') ?? '';
const cleanMenuClass = (value?: string) => value?.split(/\s+/).filter(token => token && utility(token) !== 'w-full').join(' ');

const viewport = () => {
  const visual = window.visualViewport;
  return {
    top: visual?.offsetTop ?? 0,
    left: visual?.offsetLeft ?? 0,
    width: visual?.width ?? window.innerWidth,
    height: visual?.height ?? window.innerHeight,
  };
};

const fullWidthActive = (className?: string) => {
  if (!className || typeof window === 'undefined') return false;
  let active = '';
  let minimum = -1;
  for (const token of className.split(/\s+/)) {
    const parts = token.split(':');
    const value = parts.pop()?.replace(/^!/, '') ?? '';
    if (!value.startsWith('w-')) continue;
    const breakpoint = parts.reduce((max, part) => Math.max(max, BREAKPOINTS[part] ?? 0), 0);
    if (window.innerWidth >= breakpoint && breakpoint >= minimum) {
      active = value;
      minimum = breakpoint;
    }
  }
  return active === 'w-full';
};

const initialPosition = (): Position => {
  if (typeof window === 'undefined') {
    return { top: 0, left: 0, width: 320, maxHeight: 320, maxWidth: 320, minWidth: MIN_WIDTH, side: 'bottom', ready: false };
  }
  const current = viewport();
  const maxWidth = Math.max(MIN_VIEWPORT_SIZE, current.width - PAD * 2);
  const maxHeight = Math.max(MIN_VIEWPORT_SIZE, current.height - PAD * 2);
  return {
    top: current.top + PAD,
    left: current.left + PAD,
    width: maxWidth,
    maxHeight,
    maxWidth,
    minWidth: Math.min(MIN_WIDTH, maxWidth),
    side: 'bottom',
    ready: false,
  };
};

const opposite = (side: MenuSide): MenuSide => ({ top: 'bottom', bottom: 'top', left: 'right', right: 'left' })[side] as MenuSide;

const transformOrigin = (side: MenuSide, align: MenuAlign) => {
  const end = align === 'end';
  if (side === 'top') return `bottom ${end ? 'right' : 'left'}`;
  if (side === 'bottom') return `top ${end ? 'right' : 'left'}`;
  return `${end ? 'bottom' : 'top'} ${side === 'left' ? 'right' : 'left'}`;
};

const resolvePosition = (anchor: DOMRect, menu: DOMRect, preferred: MenuSide, align: MenuAlign, fullWidth: boolean): Position => {
  const current = viewport();
  const right = current.left + current.width;
  const bottom = current.top + current.height;
  const safeWidth = Math.max(MIN_VIEWPORT_SIZE, current.width - PAD * 2);
  const safeHeight = Math.max(MIN_VIEWPORT_SIZE, current.height - PAD * 2);
  const space: Record<MenuSide, number> = {
    top: anchor.top - current.top - GAP - PAD,
    bottom: bottom - anchor.bottom - GAP - PAD,
    left: anchor.left - current.left - GAP - PAD,
    right: right - anchor.right - GAP - PAD,
  };
  const candidates = Array.from(new Set<MenuSide>([preferred, opposite(preferred), 'bottom', 'top', 'right', 'left']));
  const side = candidates.find(value => space[value] >= (value === 'top' || value === 'bottom' ? menu.height : menu.width))
    ?? candidates.reduce((best, value) => space[value] > space[best] ? value : best);
  const vertical = side === 'top' || side === 'bottom';
  const maxWidth = vertical ? safeWidth : Math.max(MIN_VIEWPORT_SIZE, Math.min(safeWidth, space[side]));
  const maxHeight = vertical ? Math.max(MIN_VIEWPORT_SIZE, Math.min(safeHeight, space[side])) : safeHeight;
  const minWidth = Math.min(Math.max(anchor.width, MIN_WIDTH), maxWidth);
  const width = fullWidth ? maxWidth : Math.min(Math.max(menu.width || minWidth, minWidth), maxWidth);
  const height = Math.min(menu.height || MIN_VIEWPORT_SIZE, maxHeight);
  let top = anchor.bottom + GAP;
  let left = align === 'end' ? anchor.right - width : anchor.left;
  if (side === 'top') top = anchor.top - GAP - height;
  if (side === 'right') {
    top = align === 'end' ? anchor.bottom - height : anchor.top;
    left = anchor.right + GAP;
  }
  if (side === 'left') {
    top = align === 'end' ? anchor.bottom - height : anchor.top;
    left = anchor.left - GAP - width;
  }
  return {
    top: clamp(top, current.top + PAD, bottom - PAD - height),
    left: clamp(left, current.left + PAD, right - PAD - width),
    width,
    maxHeight,
    maxWidth,
    minWidth,
    side,
    ready: true,
  };
};

export function Menu({
  trigger,
  items = [],
  content,
  align = 'start',
  side = 'bottom',
  className,
  menuClassName,
  itemClassName,
  closeOnSelect = true,
  onOpenChange,
  role = 'menu',
  ariaLabel,
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const [position, setPosition] = useState<Position>(initialPosition);
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const frameRef = useRef<number | null>(null);
  const menuClasses = useMemo(() => cleanMenuClass(menuClassName), [menuClassName]);
  const actionable = useMemo(() => items.filter((item): item is MenuItemProps => !isSeparator(item) && !item.disabled), [items]);
  const isFullWidth = fullWidthActive(menuClassName);

  const close = useCallback(() => {
    setOpen(false);
    setFocusIndex(-1);
    triggerRef.current?.focus?.();
  }, []);
  const openMenu = useCallback(() => {
    setPosition(initialPosition());
    setFocusIndex(-1);
    setOpen(true);
  }, []);
  const toggle = useCallback(() => open ? close() : openMenu(), [close, open, openMenu]);
  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !menuRef.current) return;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      if (!triggerRef.current || !menuRef.current) return;
      setPosition(resolvePosition(
        triggerRef.current.getBoundingClientRect(),
        menuRef.current.getBoundingClientRect(),
        side,
        align,
        fullWidthActive(menuClassName),
      ));
    });
  }, [align, menuClassName, side]);

  useEffect(() => onOpenChange?.(open), [onOpenChange, open]);
  useIsomorphicLayoutEffect(() => { if (open) updatePosition(); }, [content, items.length, open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = ({ target }: PointerEvent) => {
      const node = target as Node;
      if (!menuRef.current?.contains(node) && !triggerRef.current?.contains(node)) close();
    };
    const onKeyDown = ({ key }: globalThis.KeyboardEvent) => { if (key === 'Escape') close(); };
    const visual = window.visualViewport;
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updatePosition);
    if (menuRef.current) resizeObserver?.observe(menuRef.current);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    visual?.addEventListener('resize', updatePosition);
    visual?.addEventListener('scroll', updatePosition);
    return () => {
      resizeObserver?.disconnect();
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      visual?.removeEventListener('resize', updatePosition);
      visual?.removeEventListener('scroll', updatePosition);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [close, open, updatePosition]);

  useEffect(() => {
    if (!open || focusIndex < 0) return;
    listRef.current?.querySelectorAll<HTMLElement>("[role='menuitem']:not(:disabled)")[focusIndex]?.focus();
  }, [focusIndex, open]);

  const onTriggerKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    const first = event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown';
    const last = event.key === 'ArrowUp';
    if (!first && !last) return;
    event.preventDefault();
    openMenu();
    if (actionable.length) setFocusIndex(first ? 0 : actionable.length - 1);
  }, [actionable.length, openMenu]);

  const onMenuKeyDown = useCallback((event: KeyboardEvent<HTMLUListElement>) => {
    const count = actionable.length;
    if (event.key === 'Tab') return close();
    if (!count) return;
    const movements: Record<string, () => void> = {
      ArrowDown: () => setFocusIndex(index => (index + 1) % count),
      ArrowUp: () => setFocusIndex(index => (index - 1 + count) % count),
      Home: () => setFocusIndex(0),
      End: () => setFocusIndex(count - 1),
    };
    if (movements[event.key]) {
      event.preventDefault();
      movements[event.key]();
    }
  }, [actionable.length, close]);

  const triggerElement = isValidElement(trigger)
    ? cloneElement(trigger as ReactElement<any>, {
        ref: (node: HTMLElement | null) => { triggerRef.current = node; },
        'aria-haspopup': role,
        'aria-expanded': open,
        onClick: toggle,
        onKeyDown: onTriggerKeyDown,
        className: cn('touch-manipulation rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary/35', (trigger as ReactElement<any>).props.className, className),
      })
    : <button ref={triggerRef as any} type="button" aria-haspopup={role} aria-expanded={open} onClick={toggle} onKeyDown={onTriggerKeyDown} className={cn('touch-manipulation rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary/35', className)}>{trigger}</button>;

  const style: CSSProperties = {
    position: 'fixed',
    top: position.top,
    left: position.left,
    width: isFullWidth || position.ready ? position.width : undefined,
    minWidth: position.minWidth,
    maxWidth: position.maxWidth,
    maxHeight: position.maxHeight,
    boxSizing: 'border-box',
    transformOrigin: transformOrigin(position.side, align),
    visibility: position.ready ? 'visible' : 'hidden',
  };
  const portalContainer = typeof document === 'undefined' ? null : document.fullscreenElement ?? document.body;

  return (
    <>
      {triggerElement}
      {open && portalContainer && createPortal(
        <div
          ref={menuRef}
          role={role}
          aria-label={ariaLabel}
          style={style}
          className={cn(
            'z-[1000] flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#111116]/95 text-white shadow-[0_8px_24px_rgba(0,0,0,0.4)] backdrop-blur-2xl will-change-transform animate-[scale-in_150ms_ease-out]',
            menuClasses,
          )}
        >
          {content}
          {items.length > 0 && (
            <ul ref={listRef} onKeyDown={onMenuKeyDown} className="flex-1 overflow-auto overscroll-contain py-1">
              {items.map((entry, index) => isSeparator(entry) ? (
                <li key={index} role="separator" className="my-1 border-t border-white/10" />
              ) : (
                <li key={index} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    disabled={entry.disabled}
                    tabIndex={-1}
                    onClick={() => {
                      if (entry.disabled) return;
                      entry.onClick?.();
                      if (closeOnSelect) close();
                    }}
                    className={cn(
                      'flex min-h-11 w-full items-center gap-2 border border-transparent px-3 py-2 text-start text-sm transition focus:outline-none',
                      entry.disabled ? 'cursor-not-allowed text-white/35' : entry.destructive ? 'text-red-400 hover:bg-red-500/10 focus:bg-red-500/10' : 'text-white hover:border-primary/20 hover:bg-primary/10 focus:bg-primary/10',
                      itemClassName,
                    )}
                  >
                    {entry.icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center text-white/55">{entry.icon}</span>}
                    <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                    {entry.shortcut && <kbd className="shrink-0 font-mono text-xs text-white/40">{entry.shortcut}</kbd>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>,
        portalContainer,
      )}
    </>
  );
}
